<script setup lang="ts">
  import { ref, computed, type CSSProperties } from 'vue';
  import { FieldSize } from './Field.types';
  import { LABEL_VARIANT_BY_SIZE, INNER_TEXT_VARIANT_BY_SIZE } from './lib';
  import { TypoVariant, Typo } from '@/components';

  interface FieldProps {
    filled?: boolean;
    withDropdown?: boolean;
    label?: string;
    note?: string;
    size?: `${FieldSize}`;
    disabled?: boolean;
    readonly?: boolean;
    error?: string | boolean;
    required?: boolean;
    labelOffset?: number;
    labelOffsetLeft?: number;
    labelMaxWidth?: number;
    fullWidth?: boolean;
    nowrap?: boolean;
  }

  const props = withDefaults(defineProps<FieldProps>(), {
    filled: false,
    withDropdown: false,
    label: '',
    note: '',
    size: FieldSize.S,
    disabled: false,
    readonly: false,
    error: '',
    required: false,
    labelOffset: 0,
    labelOffsetLeft: 0,
    labelMaxWidth: 0,
    fullWidth: true,
    nowrap: true,
  });

  interface WrapperEmits {
    (e: 'focus'): void;
    (e: 'blur'): void;
  }

  const emit = defineEmits<WrapperEmits>();

  const isFocused = ref(false);

  const onFieldTrigger = (e) => {
    // TODO
    // хорошей реализацией есть:
    // if (e.target !== document.activeElement) {
    //   e.preventDefault();
    // }
    // но необходимо переписать ивенты кликов селекта
    // при текущей реализации мы должны стопать обработку кликов когда field используется в элементах с дропдаунами
    // данный хак покрывает большинство кейсов, однако для datepicker остается область вокруг, где клик приводит к морганию
    if (isFocused.value && props.withDropdown) {
      e.preventDefault();
    }
  };

  const focus = () => {
    isFocused.value = true;
    emit('focus');
  };

  const blur = () => {
    isFocused.value = false;
    emit('blur');
  };

  const innerTextVariant = computed(() => INNER_TEXT_VARIANT_BY_SIZE[props.size] || TypoVariant.BodyMRegular);

  const labelVariant = computed(() => {
    if (!isFocused.value && !props.filled) {
      return innerTextVariant.value;
    }

    return LABEL_VARIANT_BY_SIZE[props.size] || TypoVariant.BodyXSRegular;
  });

  const upward = computed(() => isFocused.value || props.filled);

  const labelStyle = computed(() => {
    const style: CSSProperties = {};
    if (props.labelOffset && !upward.value) {
      style.top = `${props.labelOffset}px`;
      style.transform = 'none';
      style.maxHeight = `calc(100% - ${props.labelOffset}px - 2px)`;
    }

    if (props.labelOffsetLeft && !upward.value) {
      style.left = `${props.labelOffsetLeft}px`;
    }

    if (props.labelMaxWidth && !upward.value) {
      style.maxWidth = `${props.labelMaxWidth}px`;
    }
    return style;
  });

  const bottomTextVariant = computed(() =>
    props.size === FieldSize.XL ? TypoVariant.BodyLRegular : TypoVariant.BodyMRegular,
  );

  const errorText = computed(() => (props.error && typeof props.error === 'string' ? props.error : ''));
  const bottomText = computed(() => errorText.value || props.note);

  defineExpose({
    focus,
    blur,
  });
</script>

<template>
  <div
    :class="[
      'tl-field',
      `tl-field_size-${size}`,
      {
        'tl-field_focused': isFocused,
        'tl-field_disabled': disabled,
        'tl-field_readonly': readonly,
        'tl-field_error': error,
        'tl-field_full-width': fullWidth,
      },
    ]"
  >
    <div class="tl-field__content-wrapper">
      <div class="tl-field__label-wrapper">
        <Typo
          v-if="label"
          tag="span"
          :variant="labelVariant"
          :style="labelStyle"
          data-test-id="label"
          class="tl-field__label"
          :class="['tl-field__label', { 'tl-field__label_nowrap': upward || nowrap, 'tl-field__label_upward': upward }]"
        >
          {{ label }}
          <Typo v-if="required" variant="body-xs-400" class="tl-field__label-required">*</Typo>
        </Typo>
      </div>

      <label
        :aria-disabled="disabled"
        class="tl-field__content"
        @mousedown="onFieldTrigger"
        @touchstart="onFieldTrigger"
        v-on="$listeners"
      >
        <slot />
      </label>
    </div>

    <slot name="append" />

    <Typo
      v-if="bottomText"
      :variant="bottomTextVariant"
      data-test-id="bottom-text"
      :class="['tl-field__bottom-text', { 'tl-field__bottom-text_error': errorText }]"
    >
      {{ bottomText }}
    </Typo>
  </div>
</template>

<style lang="scss" scoped>
  .tl-field {
    $root: &;
    $content-wrapper: '#{&}__content-wrapper';
    $label-padding: var(--spacing-xxs);

    display: inline-flex;
    flex-direction: column;
    position: relative;

    @function get-width($value) {
      @return calc(100% - $value - $label-padding * 2 - 2px);
    }

    &_full-width {
      width: 100%;
    }

    &_focused > #{$content-wrapper} {
      #{$root}__content {
        border-color: var(--color-brand-secondary);
      }
    }

    &_error > #{$content-wrapper} {
      #{$root}__content {
        border-color: var(--color-rose-50);
      }
    }

    &_readonly > #{$content-wrapper} {
      #{$root}__label {
        background-color: transparent;
      }

      #{$root}__content {
        background-color: transparent;
        border-color: transparent;
        cursor: default;
      }
    }

    &_size {
      &-s > #{$content-wrapper} {
        #{$root}__label {
          left: calc(var(--spacing-xxs) + var(--spacing-xxxs));
          max-width: get-width(calc(var(--spacing-xxs) + var(--spacing-xxxs)));
        }
      }

      &-m > #{$content-wrapper} {
        #{$root}__label {
          left: var(--spacing-xs);
          max-width: get-width(var(--spacing-xs));
        }
      }

      &-l > #{$content-wrapper},
      &-xl > #{$content-wrapper} {
        #{$root}__label {
          left: var(--spacing-s);
          max-width: get-width(var(--spacing-s));
        }
      }
    }

    &_disabled {
      & > #{$content-wrapper} {
        #{$root}__label,
        #{$root}__label-required {
          background-color: transparent;
          color: var(--color-text-tertiary);
        }

        #{$root}__content {
          background: var(--color-neutral-10);
          border-color: var(--color-neutral-10);
          cursor: default;
        }
      }

      & > #{$root}__bottom-text {
        color: var(--color-text-tertiary);
      }
    }

    &__label-wrapper {
      display: flex;
      height: 100%;
      pointer-events: none;
      position: absolute;
      transition: var(--transition-default);
      width: 100%;
      z-index: 1;
    }

    &__label {
      $padding-value: 0 $label-padding;

      color: var(--color-text-secondary);
      display: block;
      max-height: 100%;
      max-width: 100%;
      overflow: hidden;
      padding: $padding-value;
      position: absolute;
      text-align: left;
      text-overflow: ellipsis;
      top: 50%;
      transform: translateY(-50%);
      transition: var(--transition-default);

      &_upward {
        background-color: var(--color-bg-primary);
        top: 0;
      }

      &_nowrap {
        white-space: nowrap;
      }

      &-required {
        color: var(--color-rose-40);
        display: inline-block;
      }
    }

    &__content-wrapper {
      position: relative;
    }

    &__content {
      align-items: center;
      border: 1px solid var(--color-neutral-20);
      border-radius: var(--rounding-s);
      box-sizing: border-box;
      display: block;
      overflow: hidden;
      position: relative;
      transition: var(--transition-default);
    }

    &__bottom-text {
      color: var(--color-text-secondary);
      margin-top: var(--spacing-xs);
      transition: var(--transition-default);

      &_error {
        color: var(--color-rose-50);
      }
    }
  }
</style>
